import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import zxcvbn from "zxcvbn";
import { Eye, EyeOff } from "lucide-react";
import { publicApi } from "../hooks/useApi";
import { useAuthContext } from "../context/AuthContext";

const STRENGTH_LABELS = ["Very weak", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLORS = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-lime-500", "bg-green-500"];

export default function SignUpPage() {
  const navigate = useNavigate();
  const { refresh } = useAuthContext();
  const [params] = useSearchParams();
  const [step, setStep] = useState(params.get("step") === "verify" ? "verify" : "register");
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const strength = useMemo(() => (password ? zxcvbn(password, [email]).score : -1), [password, email]);

  async function submitRegister(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await publicApi.post("/auth/register", { email, password });
      sessionStorage.setItem(`eb_reg_token_${email.toLowerCase()}`, res.data.registration_token || "");
      toast.success("Check your email for a verification code.");
      setStep("verify");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "already_registered") {
        setError(detail.message);
      } else {
        setError(detail?.message || "Could not create your account. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitVerify(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const registration_token = sessionStorage.getItem(`eb_reg_token_${email.toLowerCase()}`);
      await publicApi.post("/auth/register/verify", { email, code: code.trim(), registration_token });
      sessionStorage.removeItem(`eb_reg_token_${email.toLowerCase()}`);
      const { role } = await refresh();
      toast.success("Account verified.");
      navigate(role === "admin" ? "/operator" : "/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail?.message || "That code didn't match. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setError("");
    try {
      await publicApi.post("/auth/register/resend", { email });
      toast.success("A new code is on its way.");
      setCooldown(60);
      const timer = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) { clearInterval(timer); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err?.response?.data?.detail?.message || "Could not resend the code right now.");
    }
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-12" data-testid="sign-up-page">
      <div className="text-center mb-8">
        <p className="font-display text-xl tracking-[0.2em] uppercase text-white">Endure Beyond</p>
        <p className="text-white/50 text-sm mt-2">{step === "verify" ? "Verify your email" : "Create your account"}</p>
      </div>

      {step === "register" && (
        <form onSubmit={submitRegister} className="w-full max-w-sm space-y-4" data-testid="sign-up-register-form">
          {error && <p data-testid="sign-up-error" className="text-sm text-red-400">{error}</p>}
          <input
            type="email" required placeholder="Email" aria-label="Email address" value={email}
            onChange={(e) => setEmail(e.target.value)} data-testid="sign-up-email-input"
            className="w-full bg-surface border border-border px-4 py-3 text-sm"
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"} required minLength={8} placeholder="Password (min 8 characters)" aria-label="Password, minimum 8 characters" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="sign-up-password-input"
              className="w-full bg-surface border border-border px-4 py-3 pr-11 text-sm"
            />
            <button
              type="button" onClick={() => setShowPassword((v) => !v)} data-testid="sign-up-password-toggle-visibility"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {password && (
            <div data-testid="sign-up-password-strength" className="space-y-1">
              <div className="flex gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className={`h-1 flex-1 rounded-full ${i <= strength ? STRENGTH_COLORS[strength] : "bg-white/10"}`} />
                ))}
              </div>
              <p className="text-xs text-white/50">{STRENGTH_LABELS[strength]}</p>
            </div>
          )}
          <button
            type="submit" disabled={busy} data-testid="sign-up-submit-button"
            className="w-full bg-foreground text-background px-5 py-3 text-sm uppercase tracking-wide disabled:opacity-50"
          >
            {busy ? "Creating Account…" : "Create Account"}
          </button>
          <p className="text-xs text-white/50 text-center pt-2">
            Already have an account? <Link to="/sign-in" data-testid="sign-up-sign-in-link" className="underline">Sign in</Link>
          </p>
        </form>
      )}

      {step === "verify" && (
        <form onSubmit={submitVerify} className="w-full max-w-sm space-y-4" data-testid="sign-up-verify-form">
          {error && <p data-testid="sign-up-verify-error" className="text-sm text-red-400">{error}</p>}
          <p className="text-sm text-white/60">We sent a 6-digit code to <span className="text-white">{email}</span>.</p>
          <input
            inputMode="numeric" autoComplete="one-time-code" placeholder="000000" aria-label="6-digit verification code" value={code}
            onChange={(e) => setCode(e.target.value)} data-testid="sign-up-code-input"
            className="w-full bg-surface border border-border px-4 py-3 text-lg tracking-[0.3em]"
          />
          <button
            type="submit" disabled={busy} data-testid="sign-up-verify-button"
            className="w-full bg-foreground text-background px-5 py-3 text-sm uppercase tracking-wide disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Verify & Continue"}
          </button>
          <button
            type="button" onClick={resendCode} disabled={cooldown > 0} data-testid="sign-up-resend-button"
            className="w-full text-xs text-white/50 underline underline-offset-2 disabled:opacity-40"
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </form>
      )}
    </div>
  );
}
